import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Eye, 
  Calendar, 
  DollarSign, 
  FileText, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Filter,
  Download,
  Search,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, formatDate } from '../../services/api';

const BondOversight = () => {
  const { getDisplayName } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for bonds under management
  const mockBonds = [
    {
      id: 1,
      name: 'Green Infrastructure Bond 2024-A',
      isin: 'MY1234567890',
      issuer: 'Sustainable Development Corp',
      productType: 'Fixed Rate Notes',
      currentStatus: 'Active',
      issueDate: '2024-01-15',
      maturityDate: '2027-01-15',
      nominalAmount: 50000000,
      outstandingAmount: 48500000,
      couponRate: 4.5,
      nextPaymentDate: '2025-01-15',
      totalInvestors: 156,
      lastActivity: '2025-01-10'
    },
    {
      id: 2,
      name: 'Tech Development Sukuk',
      isin: 'MY0987654321',
      issuer: 'Technology Innovation Fund',
      productType: 'Sukuk',
      currentStatus: 'Active',
      issueDate: '2023-06-20',
      maturityDate: '2026-06-20',
      nominalAmount: 75000000,
      outstandingAmount: 72000000,
      couponRate: 5.2,
      nextPaymentDate: '2025-02-20',
      totalInvestors: 203,
      lastActivity: '2025-01-12'
    },
    {
      id: 3,
      name: 'Infrastructure Bond 2022-A',
      isin: 'MY1122334455',
      issuer: 'National Infrastructure Ltd',
      productType: 'Fixed Rate Notes',
      currentStatus: 'Matured',
      issueDate: '2022-03-10',
      maturityDate: '2025-03-10',
      nominalAmount: 100000000,
      outstandingAmount: 0,
      couponRate: 3.8,
      nextPaymentDate: null,
      totalInvestors: 284,
      lastActivity: '2025-03-10'
    },
    {
      id: 4,
      name: 'Corporate Expansion Bond',
      isin: 'MY5566778899',
      issuer: 'Metro Development Sdn Bhd',
      productType: 'Fixed Rate Notes',
      currentStatus: 'Active',
      issueDate: '2024-09-05',
      maturityDate: '2029-09-05',
      nominalAmount: 30000000,
      outstandingAmount: 30000000,
      couponRate: 4.2,
      nextPaymentDate: '2025-03-05',
      totalInvestors: 98,
      lastActivity: '2025-01-08'
    },
    {
      id: 5,
      name: 'Renewable Energy Sukuk Series B',
      isin: 'MY9988776655',
      issuer: 'Green Energy Solutions',
      productType: 'Sukuk',
      currentStatus: 'Suspended',
      issueDate: '2023-11-15',
      maturityDate: '2026-11-15',
      nominalAmount: 45000000,
      outstandingAmount: 44200000,
      couponRate: 4.8,
      nextPaymentDate: '2025-05-15',
      totalInvestors: 127,
      lastActivity: '2024-12-20'
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'Active', label: 'Active' },
    { value: 'Matured', label: 'Matured' },
    { value: 'Suspended', label: 'Suspended' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'Fixed Rate Notes', label: 'Fixed Rate Notes' },
    { value: 'Sukuk', label: 'Sukuk' }
  ];

  const getStatusBadge = (status) => {
    const config = {
      'Active': { 
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      },
      'Matured': { 
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Calendar
      },
      'Suspended': { 
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: AlertTriangle
      }
    };
    
    const { color, icon: Icon } = config[status] || config['Active'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const filteredBonds = mockBonds.filter(bond => {
    const matchesStatus = selectedStatus === 'all' || bond.currentStatus === selectedStatus;
    const matchesType = selectedType === 'all' || bond.productType === selectedType;
    const matchesSearch = searchTerm === '' || 
      bond.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bond.isin.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bond.issuer.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesType && matchesSearch;
  });

  const totalOutstanding = filteredBonds.reduce((sum, bond) => sum + bond.outstandingAmount, 0);
  const activeBonds = filteredBonds.filter(bond => bond.currentStatus === 'Active').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bond Oversight</h1>
              <p className="mt-2 text-sm text-gray-600">Comprehensive overview and monitoring of all bonds under management</p>
            </div>
            <button className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">
              <Download className="h-4 w-4" />
              <span>Export Report</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-blue-100">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Bonds</p>
              <p className="text-2xl font-bold text-gray-900">{filteredBonds.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Bonds</p>
              <p className="text-2xl font-bold text-gray-900">{activeBonds}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-purple-100">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-600">Outstanding Amount</p>
              <p className="text-2xl font-bold text-gray-900 truncate" title={formatCurrency(totalOutstanding)}>
                {totalOutstanding >= 1000000 ? `RM ${(totalOutstanding / 1000000).toFixed(0)}M` : formatCurrency(totalOutstanding)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 rounded-lg bg-yellow-100">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg. Coupon Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {((filteredBonds.reduce((sum, bond) => sum + bond.couponRate, 0) / filteredBonds.length) || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Bonds</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, ISIN, or issuer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <div className="sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          
          <div className="sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">Type Filter</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              {typeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bonds Table */}
      <div className="bg-white shadow rounded-lg border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Bonds Portfolio ({filteredBonds.length})</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bond Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Financial Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Key Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Investors
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBonds.map((bond) => (
                <tr key={bond.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{bond.name}</div>
                      <div className="text-sm text-gray-500">ISIN: {bond.isin}</div>
                      <div className="text-xs text-gray-400">{bond.issuer}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      {getStatusBadge(bond.currentStatus)}
                      <span className="text-xs text-gray-500">{bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      Outstanding: {formatCurrency(bond.outstandingAmount)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Nominal: {formatCurrency(bond.nominalAmount)}
                    </div>
                    <div className="text-xs text-gray-400">
                      Coupon: {bond.couponRate}% p.a.
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      Issue: {formatDate(bond.issueDate)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Maturity: {formatDate(bond.maturityDate)}
                    </div>
                    {bond.nextPaymentDate && (
                      <div className="text-xs text-blue-600">
                        Next: {formatDate(bond.nextPaymentDate)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{bond.totalInvestors}</div>
                    <div className="text-xs text-gray-500">investors</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button className="text-primary-600 hover:text-primary-700">
                        <Eye className="h-4 w-4" />
                      </button>
                      <Link
                        to="/facility-agent/payments"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Calendar className="h-4 w-4" />
                      </Link>
                      <Link
                        to="/facility-agent/maturities"
                        className="text-green-600 hover:text-green-700"
                      >
                        <TrendingUp className="h-4 w-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBonds.length === 0 && (
          <div className="px-6 py-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bonds found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default BondOversight; 